/* eslint-disable react/no-unstable-nested-components */
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  compareDateOnlyStringsDesc,
  formatDateOnlyForDisplay,
  localCalendarDateToIsoDateString,
  normalizeCalendarDayToLocalNoon,
} from "helpers/date";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useGetContractFeeScheduleQuery,
  useGetContractFeeSchedulesQuery,
  useCreateNextContractTermMutation,
  useDeleteContractTermMutation,
  IContractTermInGroup,
  RateCalculation,
  RateType,
  normalizeRateType,
  useExportFeeScheduleMutation,
  useGenerateProviderProceduresMutation,
  useUpdateContractFeeScheduleMutation,
  useActivateContractFeeScheduleMutation,
  resolveRateCalculationBaseRateDisplayName,
} from "store/queries/contractFeeSchedules";
import { authSelector } from "store/slices/auth";

export function ContractViewPage() {
  const { id, contractId } = useParams<{ id: string; contractId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // In hospital context, use contractId; in admin context, use id
  const actualContractId = contractId || id;
  const hospitalId = contractId ? id : null; // If contractId exists, then id is the hospital ID
  useDocumentTitle("Contract Details");
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { token } = useSelector(authSelector);

  const {
    data: contractData,
    isLoading,
    error,
    refetch,
  } = useGetContractFeeScheduleQuery(actualContractId!);
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({});
  const [exportFeeSchedule] = useExportFeeScheduleMutation();
  const [generateProcedures] = useGenerateProviderProceduresMutation();
  const [updateContract] = useUpdateContractFeeScheduleMutation();
  const [activateContract] = useActivateContractFeeScheduleMutation();
  const [createNextTerm, { isLoading: isCreatingTerm }] =
    useCreateNextContractTermMutation();
  const [deleteContractTerm, { isLoading: isDeletingTerm }] =
    useDeleteContractTermMutation();

  const contract = contractData?.data;

  const passedTerms = (
    location.state as { termsInGroup?: IContractTermInGroup[] } | null
  )?.termsInGroup;

  const { data: contractList = [], refetch: refetchContractList } =
    useGetContractFeeSchedulesQuery(
      {
        providerId: contract?.providerId,
        payerId: contract?.payerId,
      },
      {
        skip: !contract?.providerId || contract?.payerId == null,
      }
    );

  const termsFromList = useMemo(() => {
    if (!contractList.length || !actualContractId) return undefined;
    const row = contractList.find((r) => {
      if (r.id === actualContractId) return true;
      // eslint-disable-next-line no-underscore-dangle
      return !!r._termsInGroup?.some((t) => t.id === actualContractId);
    });
    // eslint-disable-next-line no-underscore-dangle
    return row?._termsInGroup;
  }, [contractList, actualContractId]);

  /** Prefer API list so new terms appear after create; use navigation state only until list loads. */
  const termsInGroup = useMemo(() => {
    if (termsFromList && termsFromList.length > 0) return termsFromList;
    if (passedTerms && passedTerms.length > 0) return passedTerms;
    return termsFromList;
  }, [termsFromList, passedTerms]);

  const termSwitcherOptions = useMemo(() => {
    if (!termsInGroup?.length) return [];
    return [...termsInGroup]
      .sort((a, b) =>
        compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
      )
      .map((t) => ({
        label: `${formatDateOnlyForDisplay(t.effectiveDate)}${
          t.endDate ? ` – ${formatDateOnlyForDisplay(t.endDate)}` : ""
        } · ${t.status}`,
        value: t.id,
      }));
  }, [termsInGroup]);

  /** Per-term delete only when the group has 2+ terms; a lone term = whole contract (use list "delete entire contract"). */
  const hasMultipleTermsInGroup = (termsInGroup?.length ?? 0) > 1;

  const [showDeleteTermDialog, setShowDeleteTermDialog] = useState(false);
  const [showAddTermDialog, setShowAddTermDialog] = useState(false);
  const [addTermEffectiveDate, setAddTermEffectiveDate] = useState<Date | null>(
    null
  );
  const [addTermBaseRateFileId, setAddTermBaseRateFileId] = useState<
    string | null
  >(null);

  const addTermBaseFileOptions = useMemo(() => {
    if (!baseRateFiles?.data?.length) {
      return [{ label: "None (copy rates as-is)", value: "" }];
    }
    return [
      { label: "None (copy rates as-is)", value: "" },
      ...baseRateFiles.data.map((f) => ({
        label: `${f.displayName}${
          f.effectiveYear != null ? ` (${f.effectiveYear})` : ""
        }`,
        value: f.id,
      })),
    ];
  }, [baseRateFiles]);

  // Generate procedures dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [acknowledgeOverwrite, setAcknowledgeOverwrite] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any>(null);

  useEffect(() => {
    setIsLoading(isLoading);
    return () => {
      setIsLoading(false);
    };
  }, [isLoading, setIsLoading]);

  useEffect(() => {
    if (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load contract details.",
      });
    }
  }, [error, toastElement]);

  // Auto-refresh when contract is importing
  useEffect(() => {
    if (contract?.status === "Importing") {
      const interval = setInterval(() => {
        refetch();
      }, 5000);

      return () => clearInterval(interval);
    }
    return () => {};
  }, [contract?.status, refetch]);

  if (!contract) {
    return (
      <div className="card">
        <div className="text-center p-4">
          {isLoading ? "Loading contract details..." : "Contract not found"}
        </div>
      </div>
    );
  }

  const contractsListBasePath = hospitalId
    ? `/hospitals/view/${hospitalId}/contracts`
    : `/payer-rules/contracts`;

  const navigateToTerm = (newId: string) => {
    navigate(`${contractsListBasePath}/${newId}`, {
      replace: true,
      state: location.state,
    });
  };

  const handleDeleteTermConfirm = async () => {
    if (!actualContractId || !contract) return;
    const termIdToDelete = actualContractId;
    try {
      await deleteContractTerm(termIdToDelete).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Term removed",
        detail: "This contract period was deleted.",
      });
      setShowDeleteTermDialog(false);
      const { data: freshList } = await refetchContractList();
      const row = (freshList ?? []).find(
        (r) =>
          r.providerId === contract.providerId && r.payerId === contract.payerId
      );
      let nextTermId: string | null = null;
      if (row) {
        // eslint-disable-next-line no-underscore-dangle
        const terms = row._termsInGroup;
        if (terms?.length) {
          const others = terms.filter((t) => t.id !== termIdToDelete);
          if (others.length > 0) {
            nextTermId = [...others].sort((a, b) =>
              compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
            )[0].id;
          }
        } else if (row.id !== termIdToDelete) {
          nextTermId = row.id;
        }
      }
      if (nextTermId) {
        navigateToTerm(nextTermId);
      } else {
        navigate(contractsListBasePath);
      }
    } catch (err: any) {
      const status = err?.status ?? err?.originalStatus;
      const msg =
        status === 404
          ? "This term was already removed or could not be found."
          : err?.data?.messages?.[0] ||
            err?.data?.message ||
            "Could not delete this term.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: msg,
      });
    }
  };

  const handleAddTermSubmit = async () => {
    const iso = localCalendarDateToIsoDateString(addTermEffectiveDate);
    if (!iso) {
      toastElement?.show({
        severity: "error",
        summary: "Validation",
        detail: "Select an effective date for the new term.",
      });
      return;
    }
    try {
      const res = await createNextTerm({
        id: actualContractId!,
        effectiveDate: iso,
        ...(addTermBaseRateFileId
          ? { newBaseRateFileId: addTermBaseRateFileId }
          : {}),
      }).unwrap();
      const newId = res.data?.id;
      await refetchContractList();
      toastElement?.show({
        severity: "success",
        summary: "Term created",
        detail: "The new contract term is active.",
      });
      setShowAddTermDialog(false);
      setAddTermEffectiveDate(null);
      setAddTermBaseRateFileId(null);
      if (newId) navigateToTerm(newId);
      else refetch();
    } catch (err: any) {
      const msg =
        err?.data?.messages?.[0] ||
        err?.data?.message ||
        "Could not create the next term.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: msg,
      });
    }
  };

  const statusSeverityMap = {
    Draft: "info",
    Active: "success",
    Inactive: "warning",
    Expired: "danger",
    Importing: "warning",
  } as const;

  const getButtonLabel = (status?: string, dryRunResult?: any) => {
    if (status === "Draft") {
      return dryRunResult ? "Activate Contract" : "Activate";
    }
    return dryRunResult ? "Generate" : "Generate";
  };

  const getRateTypeForDisplay = (calc: RateCalculation): RateType =>
    normalizeRateType(calc.rateType);

  const rateCalculationBodyTemplate = (rowData: RateCalculation) => {
    const baseRateFile = baseRateFiles?.data?.find(
      (f) => f.id === rowData.baseRateFileId
    );
    const procedureCount = baseRateFile?.metadata?.rowCount;
    const baseRateFileName = resolveRateCalculationBaseRateDisplayName(
      rowData,
      baseRateFiles?.data
    );
    const procedureText =
      procedureCount !== undefined
        ? ` (${procedureCount.toLocaleString()} procedures)`
        : "";
    const rateType = getRateTypeForDisplay(rowData);

    return (
      <div className="text-900">
        {rateType === "unit"
          ? `$${(rowData.ratePercent / 100).toFixed(
              2
            )} per ${baseRateFileName}${procedureText}`
          : `${rowData.ratePercent}% of ${baseRateFileName}${procedureText}`}
      </div>
    );
  };

  return (
    <div className="card">
      {/* Outdated Procedures Warning */}
      {contract.proceduresOutdated && contract.status === "Active" && (
        <Message
          severity="warn"
          className="mb-4"
          text="The base rate files for this contract have been updated since the provider procedures were last generated. To ensure accurate pricing, please regenerate the procedures using the 'Re-generate Procedures' button."
        />
      )}

      <div className="flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="m-0 mb-2">
            {contract.provider?.name} - {contract.payer?.name}
          </h2>
          <div className="flex align-items-center gap-2">
            <Badge
              value={contract.status}
              severity={statusSeverityMap[contract.status]}
            />
            {contract.proceduresOutdated && contract.status === "Active" && (
              <span title="Base rates have been updated. Procedures need regeneration.">
                <Badge value="Outdated" severity="warning" className="ml-2" />
              </span>
            )}
            {contract.status === "Importing" && (
              <span className="text-warning ml-2">
                <i className="pi pi-spin pi-spinner mr-2" />
                Fee schedule is being imported...
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 align-items-center flex-wrap">
          {termSwitcherOptions.length > 1 && (
            <Dropdown
              value={actualContractId}
              options={termSwitcherOptions}
              onChange={(e) => navigateToTerm(e.value)}
              placeholder="Switch term"
              className="min-w-12rem"
            />
          )}
          {contract.status === "Active" && (
            <Button
              label="Add term"
              icon="pi pi-calendar-plus"
              className="p-button-outlined"
              onClick={() => {
                setAddTermEffectiveDate(null);
                setAddTermBaseRateFileId(null);
                setShowAddTermDialog(true);
              }}
            />
          )}
          {hasMultipleTermsInGroup && (
            <Button
              label="Delete this term"
              icon="pi pi-trash"
              className="p-button-outlined p-button-danger"
              disabled={contract.status === "Importing" || isDeletingTerm}
              onClick={() => setShowDeleteTermDialog(true)}
            />
          )}
          <Button
            label="Edit"
            icon="pi pi-pencil"
            className="p-button-outlined"
            onClick={() => {
              const editUrl = hospitalId
                ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}/edit`
                : `/payer-rules/contracts/${actualContractId}/edit`;
              navigate(editUrl);
            }}
          />
          <Button
            label="Preview"
            icon="pi pi-eye"
            className="p-button-outlined"
            onClick={() => {
              const previewUrl = hospitalId
                ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}/preview`
                : `/payer-rules/contracts/${actualContractId}/preview`;
              navigate(previewUrl);
            }}
          />
          <Button
            label="Export"
            icon="pi pi-download"
            className="p-button-outlined"
            onClick={async () => {
              try {
                setIsLoading(true);
                const result = await exportFeeSchedule({
                  contractId: actualContractId!,
                  params: {},
                }).unwrap();

                // Create a download link for the CSV
                const url = window.URL.createObjectURL(result);
                const a = document.createElement("a");
                a.href = url;
                a.download = `fee-schedule-${
                  contract.provider?.name || "provider"
                }-${contract.payer?.name || "payer"}-${
                  new Date().toISOString().split("T")[0]
                }.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                toastElement?.show({
                  severity: "success",
                  summary: "Export Complete",
                  detail: "Fee schedule CSV exported successfully.",
                });
              } catch (error) {
                toastElement?.show({
                  severity: "error",
                  summary: "Export Failed",
                  detail: "Failed to export fee schedule.",
                });
              } finally {
                setIsLoading(false);
              }
            }}
          />
          {contract.status === "Draft" && (
            <Button
              label="Activate Contract"
              icon="pi pi-check-circle"
              className="p-button-success"
              onClick={() => setShowGenerateDialog(true)}
            />
          )}
          {contract.status === "Active" && (
            <Button
              label="Re-generate Procedures"
              icon="pi pi-sync"
              className="p-button-outlined"
              onClick={() => setShowGenerateDialog(true)}
            />
          )}
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid mb-4">
        <div className="col-12 md:col-6">
          <Card title="Contract Details" className="h-full">
            <div className="grid">
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Provider
                </label>
                <div className="text-900">
                  {contract.provider?.name || "N/A"}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">Payer</label>
                <div className="text-900">{contract.payer?.name || "N/A"}</div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Effective Date
                </label>
                <div className="text-900">
                  {formatDateOnlyForDisplay(contract.effectiveDate)}
                </div>
              </div>
              {contract.endDate && (
                <div className="col-6">
                  <label className="block text-600 font-medium mb-1">
                    End Date
                  </label>
                  <div className="text-900">
                    {formatDateOnlyForDisplay(contract.endDate)}
                  </div>
                </div>
              )}
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Next Update Date
                </label>
                <div className="text-900">
                  {contract.nextUpdateDate
                    ? formatDateOnlyForDisplay(contract.nextUpdateDate)
                    : "Not set"}
                </div>
              </div>
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <div className="grid">
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Created
                </label>
                <div className="text-900 text-sm">
                  {new Date(contract.createdAt).toLocaleString()}
                </div>
              </div>
              <div className="col-6">
                <label className="block text-600 font-medium mb-1">
                  Last Updated
                </label>
                <div className="text-900 text-sm">
                  {new Date(contract.updatedAt).toLocaleString()}
                </div>
              </div>
              <div className="col-12">
                <label className="block text-600 font-medium mb-1">
                  Created By
                </label>
                <div className="text-900">
                  {contract.createdBy?.email || "System"}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Notes */}
      {contract.notes && (
        <Card title="Notes" className="mb-4">
          <div className="text-900 white-space-pre-wrap">{contract.notes}</div>
        </Card>
      )}

      {/* Medicare Advantage Payers */}
      {contract.payer?.type === "OriginalMedicare" &&
        contract.medicareAdvantagePlans &&
        contract.medicareAdvantagePlans.length > 0 && (
          <Card title="Medicare Advantage Payers" className="mb-4">
            <div className="flex flex-wrap gap-2">
              {contract.medicareAdvantagePlans.map((link: any) => (
                <Chip
                  key={link.medicareAdvantagePlanId}
                  label={
                    link.medicareAdvantagePlan?.name ||
                    `Payer ${link.medicareAdvantagePlanId}`
                  }
                />
              ))}
            </div>
          </Card>
        )}

      {/* Rate Calculations */}
      <Card title="Rate Calculations" className="mb-4">
        {contract.rateCalculations && contract.rateCalculations.length > 0 ? (
          <DataTable
            value={contract.rateCalculations}
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column body={rateCalculationBodyTemplate} header="Rate Details" />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No rate calculations configured for this contract.
          </div>
        )}
      </Card>

      {/* Carve-Outs */}
      <Card title="Carve-Out Files" className="mb-4">
        {contract.carveOutFiles && contract.carveOutFiles.length > 0 ? (
          <DataTable
            value={contract.carveOutFiles}
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="fileName" header="File Name" />
            <Column
              field="createdAt"
              header="Upload Date"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
            />
            <Column
              header="Rows"
              body={(rowData) => {
                const rowCount = rowData.metadata?.rowCount;
                return rowCount !== undefined
                  ? `${rowCount.toLocaleString()} rows`
                  : "-";
              }}
            />
            <Column
              header="Actions"
              body={(rowData) => (
                <Button
                  icon="pi pi-download"
                  className="p-button-rounded p-button-text p-button-sm"
                  tooltip="Download"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      // Create a download link using the S3 key
                      const apiUrl =
                        (window as any).config?.apiUrl ||
                        "http://localhost:3000";
                      const response = await fetch(
                        `${apiUrl}/contract-fee-schedules/${actualContractId}/carveouts/${rowData.id}/download`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );

                      if (!response.ok) {
                        throw new Error("Download failed");
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = rowData.fileName;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      toastElement?.show({
                        severity: "error",
                        summary: "Download Failed",
                        detail: "Failed to download carveout file.",
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                />
              )}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No carve-out files uploaded for this contract.
          </div>
        )}
      </Card>

      {/* MPPR Rules */}
      <Card
        title="Multiple Procedure Payment Reduction Rulesets"
        className="mb-4"
      >
        {contract.mpdrRulesets && contract.mpdrRulesets.length > 0 ? (
          <div>
            {contract.mpdrRulesets.filter(Boolean).map((ruleset, index) => (
              <div
                key={ruleset.id}
                className={
                  index <
                  (contract.mpdrRulesets?.filter(Boolean).length ?? 0) - 1
                    ? "pb-3 mb-3 border-bottom"
                    : ""
                }
              >
                <div className="flex justify-content-between align-items-start mb-3">
                  <div>
                    <h4 className="mt-0 mb-2">
                      <a
                        href={`/payer-rules/mppr/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // Only navigate if in admin context (hospital staff can't access these pages)
                          if (!hospitalId) {
                            navigate(`/payer-rules/mppr/edit/${ruleset.id}`);
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed MPDR Rule Set"}
                      </a>
                    </h4>
                    <p className="text-600 mb-0">
                      {ruleset.provider?.name} - {ruleset.payer?.name}
                    </p>
                  </div>
                </div>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">
                      Discount Percentages
                    </div>
                    <div className="text-900">
                      <div>Rank 2: {ruleset.discountPercentForRank2}%</div>
                      <div>Rank 3: {ruleset.discountPercentForRank3}%</div>
                      <div>Rank 4: {ruleset.discountPercentForRank4}%</div>
                      <div>Rank 5+: {ruleset.discountPercentForRank5Plus}%</div>
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">CPT Codes</div>
                    <div className="text-900">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <div>
                          {ruleset.cptCodes.slice(0, 10).join(", ")}
                          {ruleset.cptCodes.length > 10 && (
                            <span className="text-600">
                              {" "}
                              and {ruleset.cptCodes.length - 10} others
                            </span>
                          )}
                        </div>
                      ) : (
                        "No codes configured"
                      )}
                    </div>
                    {ruleset.allowOnDuplicateCptCodes && (
                      <div className="mt-2">
                        <i className="pi pi-check-circle text-green-500 mr-2" />
                        <span className="text-sm">
                          Allows duplicate CPT codes
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-4 text-600">
            No MPPR rules configured for this contract.
          </div>
        )}
      </Card>

      {/* Case Rates */}
      <Card title="Case Rate Rulesets" className="mb-4">
        {contract.caseRateRulesets && contract.caseRateRulesets.length > 0 ? (
          <div>
            {contract.caseRateRulesets.filter(Boolean).map((ruleset, index) => (
              <div
                key={ruleset.id}
                className={
                  index <
                  (contract.caseRateRulesets?.filter(Boolean).length ?? 0) - 1
                    ? "pb-3 mb-3 border-bottom"
                    : ""
                }
              >
                <div className="flex justify-content-between align-items-start mb-3">
                  <div>
                    <h4 className="mt-0 mb-2">
                      <a
                        href={`/payer-rules/case-rate/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // Only navigate if in admin context (hospital staff can't access these pages)
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/case-rate/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed Case Rate Rule Set"}
                      </a>
                    </h4>
                    <p className="text-600 mb-0">
                      {ruleset.provider?.name} - {ruleset.payer?.name}
                    </p>
                  </div>
                </div>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">
                      Included CPT Codes
                    </div>
                    <div className="text-900">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <div>
                          {ruleset.cptCodes.slice(0, 10).join(", ")}
                          {ruleset.cptCodes.length > 10 && (
                            <span className="text-600">
                              {" "}
                              and {ruleset.cptCodes.length - 10} others
                            </span>
                          )}
                        </div>
                      ) : (
                        "No codes included"
                      )}
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="text-600 text-sm mb-1">
                      Excluded CPT Codes
                    </div>
                    <div className="text-900">
                      {ruleset.excludedCptCodes &&
                      ruleset.excludedCptCodes.length > 0 ? (
                        <div>
                          {ruleset.excludedCptCodes.slice(0, 10).join(", ")}
                          {ruleset.excludedCptCodes.length > 10 && (
                            <span className="text-600">
                              {" "}
                              and {ruleset.excludedCptCodes.length - 10} others
                            </span>
                          )}
                        </div>
                      ) : (
                        "No codes excluded"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-4 text-600">
            No case rate rules configured for this contract.
          </div>
        )}
      </Card>

      {/* Inpatient Rulesets */}
      <Card title="Inpatient Rulesets" className="mb-4">
        {contract.inpatientRulesets && contract.inpatientRulesets.length > 0 ? (
          <div>
            {contract.inpatientRulesets
              .filter(Boolean)
              .map((ruleset: any, index: number) => (
                <div
                  key={ruleset.id}
                  className="pb-3 mb-3"
                  style={
                    index <
                    (contract.inpatientRulesets?.filter(Boolean).length ?? 0) -
                      1
                      ? { borderBottom: "1px solid #e0e0e0" }
                      : undefined
                  }
                >
                  <div className="flex justify-content-between align-items-start mb-3">
                    <div>
                      <h4 className="mt-0 mb-2">
                        <a
                          href={`/payer-rules/inpatient-rulesets/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!hospitalId) {
                              navigate(
                                `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`
                              );
                            }
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.name || "Unnamed Inpatient Ruleset"}
                        </a>
                      </h4>
                      <p className="text-600 mb-0">
                        {ruleset.provider?.name} - {ruleset.payer?.name}
                      </p>
                    </div>
                  </div>

                  <div className="grid">
                    {ruleset.type === "FIXED_COST" && ruleset.config && (
                      <div className="col-12 md:col-6">
                        <div className="text-600 text-sm mb-1">Fixed Cost</div>
                        <div className="text-900">
                          CCR: {ruleset.config.costToChargeRatio?.toFixed(4)},
                          Threshold: $
                          {ruleset.config.fixedLossThresholdInCents != null
                            ? (
                                ruleset.config.fixedLossThresholdInCents / 100
                              ).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })
                            : "—"}
                          , Outlier:{" "}
                          {ruleset.config.outlierPercent != null
                            ? `${ruleset.config.outlierPercent}%`
                            : "80%"}
                        </div>
                        <div className="text-500 text-xs mt-1">
                          Outlier = {ruleset.config.outlierPercent ?? 80}% ×
                          (Charges × CCR − DRG Rate − Threshold)
                        </div>
                      </div>
                    )}
                    {ruleset.type === "PASS_THROUGH" && ruleset.config && (
                      <div className="col-12 md:col-6">
                        <div className="text-600 text-sm mb-1">
                          Pass Through
                        </div>
                        <div className="text-900">
                          Threshold: $
                          {ruleset.config.thresholdInCents != null
                            ? (
                                ruleset.config.thresholdInCents / 100
                              ).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })
                            : "—"}
                          , Multiplier:{" "}
                          {ruleset.config.chargeMultiplier != null
                            ? ruleset.config.chargeMultiplier.toFixed(4)
                            : "—"}
                        </div>
                        <div className="text-500 text-xs mt-1">
                          Expected = Charges ×{" "}
                          {ruleset.config.chargeMultiplier?.toFixed(2) ??
                            "Multiplier"}{" "}
                          (when charges exceed threshold)
                        </div>
                      </div>
                    )}
                    {ruleset.type === "EXCESSIVE_LOS" && ruleset.config && (
                      <div className="col-12 md:col-6">
                        <div className="text-600 text-sm mb-1">
                          Excessive LOS
                        </div>
                        <div className="text-900">
                          Formula: {ruleset.config.formula || "—"}
                        </div>
                        <div className="text-500 text-xs mt-1">
                          Expected = DRG Rate + (Per Diem × Outlier Days).
                          Params come from ProviderProcedure DRG fields.
                        </div>
                      </div>
                    )}
                    {ruleset.type === "TRANSFER" && ruleset.config && (
                      <div className="col-12 md:col-6">
                        <div className="text-600 text-sm mb-1">Transfer</div>
                        <div className="text-900">
                          LOS Formula:{" "}
                          {ruleset.config.losFormula === "LOS_PLUS_1"
                            ? "Per Diem × (LOS + 1)"
                            : "Per Diem × LOS"}
                          , Discharge Codes:{" "}
                          {ruleset.config.dischargeStatusCodes?.join(", ") ||
                            "—"}
                        </div>
                        <div className="text-500 text-xs mt-1">
                          Expected = min(DRG Rate, Per Diem ×{" "}
                          {ruleset.config.losFormula === "LOS_PLUS_1"
                            ? "(LOS + 1)"
                            : "LOS"}
                          ) when discharge status matches
                        </div>
                      </div>
                    )}
                    {ruleset.type === "STOP_LOSS" && ruleset.config && (
                      <div className="col-12 md:col-6">
                        <div className="text-600 text-sm mb-1">Stop Loss</div>
                        <div className="text-900">
                          Threshold: {ruleset.config.thresholdMultiplier}% of
                          DRG, Payment: {ruleset.config.paymentPercent}% of
                          charges
                        </div>
                        <div className="text-500 text-xs mt-1">
                          When charges &gt; DRG Rate ×{" "}
                          {ruleset.config.thresholdMultiplier}%, Expected ={" "}
                          {ruleset.config.paymentPercent}% × Total Charges
                        </div>
                      </div>
                    )}
                    {ruleset.type === "CHARGE_OUTLIER" && ruleset.config && (
                      <div className="col-12 md:col-6">
                        <div className="text-600 text-sm mb-1">
                          Charge Outlier
                        </div>
                        <div className="text-900">
                          {ruleset.config.thresholdType === "FLAT"
                            ? `Threshold: $${
                                ruleset.config.thresholdAmountInCents != null
                                  ? (
                                      ruleset.config.thresholdAmountInCents /
                                      100
                                    ).toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                    })
                                  : "—"
                              }`
                            : `Threshold: ${ruleset.config.thresholdMultiplier}× base payment`}
                          , Outlier: {ruleset.config.outlierPercent}% of excess
                          {ruleset.config.chargeFloorInCents != null &&
                            `, Floor: $${(
                              ruleset.config.chargeFloorInCents / 100
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}`}
                        </div>
                        <div className="text-500 text-xs mt-1">
                          Outlier = {ruleset.config.outlierPercent}% × (Charges
                          −{" "}
                          {ruleset.config.thresholdType === "FLAT"
                            ? "Threshold"
                            : `${ruleset.config.thresholdMultiplier}× Base`}
                          )
                        </div>
                      </div>
                    )}
                    <div className="col-12 md:col-6">
                      <div className="text-600 text-sm mb-1">DRG Codes</div>
                      <div className="text-900">
                        {ruleset.drgCodes && ruleset.drgCodes.length > 0
                          ? ruleset.drgCodes.join(", ")
                          : "No codes configured"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center p-4 text-600">
            No inpatient rulesets matched for this provider + payer combination.
          </div>
        )}
      </Card>

      {/* Exploding Fee Schedule Rulesets */}
      <Card title="Exploding Fee Schedule Rulesets" className="mb-4">
        {contract.explodingFeeScheduleRulesets &&
        contract.explodingFeeScheduleRulesets.length > 0 ? (
          <div>
            {contract.explodingFeeScheduleRulesets
              .filter(Boolean)
              .map((ruleset: any, index: number) => (
                <div
                  key={ruleset.id}
                  className="pb-3 mb-3"
                  style={
                    index <
                    (contract.explodingFeeScheduleRulesets?.filter(Boolean)
                      .length ?? 0) -
                      1
                      ? { borderBottom: "1px solid #e0e0e0" }
                      : undefined
                  }
                >
                  <div className="flex justify-content-between align-items-start mb-3">
                    <div>
                      <h4 className="mt-0 mb-2">
                        <a
                          href={`/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            if (!hospitalId) {
                              navigate(
                                `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`
                              );
                            }
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName ||
                            "Unnamed Exploding Fee Schedule Ruleset"}
                        </a>
                      </h4>
                      <p className="text-600 mb-0">
                        {ruleset.provider?.name} - {ruleset.payer?.name}
                      </p>
                    </div>
                  </div>

                  <div className="grid">
                    <div className="col-12 md:col-4">
                      <div className="text-600 text-sm mb-1">CPT Codes</div>
                      <div className="text-900">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0
                          ? ruleset.cptCodes.join(", ")
                          : "No codes configured"}
                      </div>
                    </div>
                    <div className="col-12 md:col-4">
                      <div className="text-600 text-sm mb-1">Total Rate</div>
                      <div className="text-900">
                        $
                        {(ruleset.totalRateInCents / 100).toLocaleString(
                          "en-US",
                          { minimumFractionDigits: 2 }
                        )}
                      </div>
                    </div>
                    <div className="col-12 md:col-4">
                      <div className="text-600 text-sm mb-1">Per-Code Rate</div>
                      <div className="text-900">
                        $
                        {(
                          ruleset.totalRateInCents /
                          100 /
                          ruleset.cptCodes.length
                        ).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="text-center p-4 text-600">
            No exploding fee schedule rulesets matched for this provider + payer
            combination.
          </div>
        )}
      </Card>

      {/* Contract Attachments */}
      <Card title="Contract Attachments" className="mb-4">
        {contract.attachments && contract.attachments.length > 0 ? (
          <DataTable
            value={contract.attachments}
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="fileName" header="File Name" />
            <Column
              field="fileSize"
              header="Size"
              body={(rowData) => {
                const kb = rowData.fileSize / 1024;
                return kb >= 1024
                  ? `${(kb / 1024).toFixed(2)} MB`
                  : `${kb.toFixed(2)} KB`;
              }}
            />
            <Column
              header="Uploaded By"
              body={(rowData) => rowData.uploadedBy?.name || "-"}
            />
            <Column
              field="createdAt"
              header="Upload Date"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
            />
            <Column
              header="Actions"
              body={(rowData) => (
                <Button
                  icon="pi pi-download"
                  className="p-button-rounded p-button-text p-button-sm"
                  tooltip="Download"
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      const apiUrl =
                        (window as any).config?.apiUrl ||
                        "http://localhost:3000";
                      const response = await fetch(
                        `${apiUrl}/contract-fee-schedules/${actualContractId}/attachments/${rowData.id}/download`,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        }
                      );

                      if (!response.ok) {
                        throw new Error("Download failed");
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = rowData.fileName;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      toastElement?.show({
                        severity: "error",
                        summary: "Download Failed",
                        detail: "Failed to download attachment file.",
                      });
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                />
              )}
            />
          </DataTable>
        ) : (
          <div className="text-center p-4 text-600">
            No files attached to this contract.
          </div>
        )}
      </Card>

      {/* Generate Procedures Dialog */}
      <Dialog
        header={
          contract?.status === "Draft"
            ? "Activate Contract"
            : "Generate Provider Procedures"
        }
        visible={showGenerateDialog}
        style={{ width: "600px" }}
        onHide={() => {
          setShowGenerateDialog(false);
          setDryRunResult(null);
          setAcknowledgeOverwrite(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => {
                setShowGenerateDialog(false);
                setDryRunResult(null);
                setAcknowledgeOverwrite(false);
              }}
              className="p-button-text"
            />
            <Button
              label={getButtonLabel(contract?.status, dryRunResult)}
              icon="pi pi-check"
              disabled={!acknowledgeOverwrite}
              onClick={async () => {
                try {
                  setIsLoading(true);

                  // If contract is Draft, activate it first
                  if (contract?.status === "Draft") {
                    await activateContract(actualContractId!).unwrap();
                  }

                  const result = await generateProcedures({
                    contractId: actualContractId!,
                    options: { overwriteExisting: true },
                  }).unwrap();

                  // Since the endpoint returns 202 Accepted for async processing
                  if (result.status === "processing") {
                    toastElement?.show({
                      severity: "info",
                      summary: "Import Started",
                      detail:
                        "The fee schedule import has started and will take a few minutes. The page will refresh automatically when complete.",
                      life: 10000,
                    });
                  } else {
                    // Legacy handling if backend still returns sync response
                    toastElement?.show({
                      severity: "success",
                      summary:
                        contract?.status === "Draft"
                          ? "Contract Activated"
                          : "Success",
                      detail:
                        contract?.status === "Draft"
                          ? `Contract activated and ${(
                              result.message || "procedures generated"
                            ).toLowerCase()}`
                          : result.message ||
                            "Procedures generated successfully",
                    });
                  }
                  setShowGenerateDialog(false);
                  setDryRunResult(null);
                  setAcknowledgeOverwrite(false);
                } catch (error: any) {
                  toastElement?.show({
                    severity: "error",
                    summary:
                      contract?.status === "Draft"
                        ? "Activation Failed"
                        : "Generation Failed",
                    detail: error.data?.message || "Failed to process request.",
                  });
                } finally {
                  setIsLoading(false);
                }
              }}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <Message
            severity={contract?.status === "Draft" ? "warn" : "info"}
            text={
              contract?.status === "Draft"
                ? `This will activate the contract and generate provider procedures from the calculated rates for ${contract.provider?.name} and ${contract.payer?.name}. Once activated, the contract will be used for order processing.`
                : `This will generate additional provider procedures from the contract's calculated rates for ${contract.provider?.name} and ${contract.payer?.name}.`
            }
            className="mb-4"
          />

          <div className="field-checkbox mb-4">
            <Checkbox
              inputId="acknowledgeOverwrite"
              checked={acknowledgeOverwrite}
              onChange={(e) => setAcknowledgeOverwrite(e.checked || false)}
            />
            <label htmlFor="acknowledgeOverwrite" className="ml-2">
              I acknowledge that this will overwrite any existing fee schedules
              for this provider and payer
            </label>
          </div>

          {dryRunResult && (
            <div className="mt-4">
              <h4>Preview Results</h4>
              <p>
                <strong>Total Procedures:</strong> {dryRunResult.procedureCount}
              </p>
              <p>
                <strong>Total Rate:</strong> $
                {dryRunResult.totalRate?.toFixed(2)}
              </p>

              {dryRunResult.procedures &&
                dryRunResult.procedures.length > 0 && (
                  <>
                    <h5 className="mt-3">Sample Procedures (First 10):</h5>
                    <DataTable
                      value={dryRunResult.procedures}
                      className="p-datatable-sm"
                      scrollable
                      scrollHeight="200px"
                    >
                      <Column field="cptCode" header="CPT Code" />
                      <Column field="description" header="Description" />
                      <Column
                        field="priceInCents"
                        header="Price"
                        body={(rowData) =>
                          `$${(rowData.priceInCents / 100).toFixed(2)}`
                        }
                      />
                    </DataTable>
                  </>
                )}
            </div>
          )}
        </div>
      </Dialog>

      <Dialog
        header="Delete this term?"
        visible={showDeleteTermDialog && hasMultipleTermsInGroup}
        style={{ width: "520px" }}
        onHide={() => {
          if (!isDeletingTerm) setShowDeleteTermDialog(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-text"
              disabled={isDeletingTerm}
              onClick={() => setShowDeleteTermDialog(false)}
            />
            <Button
              label="Delete term"
              className="p-button-danger"
              icon="pi pi-trash"
              loading={isDeletingTerm}
              disabled={isDeletingTerm}
              onClick={handleDeleteTermConfirm}
            />
          </div>
        }
      >
        <p className="text-900 mb-2">
          <strong>{contract.provider?.name}</strong>
          {" → "}
          <strong>{contract.payer?.name}</strong>
        </p>
        <p className="text-600 mb-3">
          Period: {formatDateOnlyForDisplay(contract.effectiveDate)}
          {contract.endDate
            ? ` – ${formatDateOnlyForDisplay(contract.endDate)}`
            : " (open-ended)"}
          . Status: {contract.status}.
        </p>
        <p className="text-600 mb-0">
          This removes contract-backed prices and linked rules for{" "}
          <strong>this period only</strong>. Claims and estimates for this date
          range may be affected. Other terms in the same contract are unchanged.
          This does not delete the entire multi-year contract unless this was
          the last term.
        </p>
      </Dialog>

      <Dialog
        header="Add contract term"
        visible={showAddTermDialog}
        style={{ width: "480px" }}
        onHide={() => {
          if (!isCreatingTerm) setShowAddTermDialog(false);
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              className="p-button-text"
              disabled={isCreatingTerm}
              onClick={() => setShowAddTermDialog(false)}
            />
            <Button
              label="Create term"
              loading={isCreatingTerm}
              disabled={isCreatingTerm}
              onClick={handleAddTermSubmit}
            />
          </div>
        }
      >
        <p className="text-600 mb-3">
          The current term becomes inactive ending on the new term&apos;s
          effective date. Optional base rate file swaps older-year lines for the
          same logical file name.
        </p>
        <div className="field">
          <label className="block mb-2 font-medium">Effective date *</label>
          <Calendar
            value={addTermEffectiveDate}
            onChange={(e) =>
              setAddTermEffectiveDate(
                normalizeCalendarDayToLocalNoon(e.value as Date | null)
              )
            }
            showIcon
            className="w-full"
            dateFormat="yy-mm-dd"
          />
        </div>
        <div className="field">
          <label className="block mb-2 font-medium">
            Optional: new base rate file
          </label>
          <Dropdown
            value={addTermBaseRateFileId || ""}
            options={addTermBaseFileOptions}
            onChange={(e) =>
              setAddTermBaseRateFileId(e.value ? String(e.value) : null)
            }
            placeholder="Swap older-year lines to this file"
            className="w-full"
            filter
            showClear
          />
        </div>
      </Dialog>
    </div>
  );
}

export default ContractViewPage;
